import type {
  RiskLevel,
  RiskLevelNoRisk,
  RiskMatrix,
} from "./risk-matrix.type";
import type { UseGetOptions, UseListOptions } from "@/hooks/queries";
import type { Nullable } from "@/types";

import { useTranslation } from "react-i18next";

import { useGetProfileLocale } from "@/entities/profile";
import { useGet, useList } from "@/hooks/queries";

export const useGetRiskMatrix = (
  id?: Nullable<string | number>,
  version?: Nullable<number>,
  options?: UseGetOptions<RiskMatrix>
) => {
  const locale = useGetProfileLocale();

  const queryOptions: UseGetOptions<RiskMatrix> = {
    ...options,
    neverStale: true,
    persist: true, // persist data in local storage
    queryKeyExtraParams: [`v${version}`, locale], // add version and locale to make sure the query is invalidated when these values change
  };

  const { data: riskMatrix, ...resultQuery } = useGet<RiskMatrix>(
    "riskMatrices",
    id,
    queryOptions
  );

  return {
    ...resultQuery,
    riskMatrix: { risks: [], ...riskMatrix },
  };
};

export const useListRiskMatrices = (options?: UseListOptions<RiskMatrix>) => {
  const locale = useGetProfileLocale();

  const queryOptions: UseListOptions<RiskMatrix> = {
    ...options,
    staleTime: 1000 * 60 * 60, // 1h
    persist: true, // persist data in local storage
    queryKeyExtraParams: [locale], // add locale to make sure the query is invalidated when these values change
  };

  const { data, ...resultQuery } = useList<RiskMatrix>(
    "riskMatrices",
    queryOptions
  );

  const riskMatrices = data?.data ?? [];

  return {
    ...resultQuery,
    riskMatrices,
  };
};

export const useGetDefaultRiskMatrix = (
  options?: Omit<UseListOptions<RiskMatrix>, "params">
) => {
  const { riskMatrices, ...resultQuery } = useListRiskMatrices({
    ...options,
    params: { "filter[is_default]": true },
  });

  // The API should return only one default risk matrix
  const riskMatrix = riskMatrices[0] ? riskMatrices[0] : undefined;

  return {
    ...resultQuery,
    riskMatrix,
  };
};

export const useGetDefaultRiskLevels = () => {
  const { t } = useTranslation();
  const { riskMatrix, ...queryResult } = useGetDefaultRiskMatrix();

  const riskLevels = riskMatrix?.risklevels ?? [];

  const noRisk = {
    name: t("riskMatrix:noRisk"),
    color: "#b7b7b7",
    value: null,
  };

  return {
    ...queryResult,
    riskLevels,
    noRisk,
  };
};

export const useGetDefaultRiskLevelsWithNoRisk = () => {
  const { riskLevels, noRisk, ...queryResult } = useGetDefaultRiskLevels();

  const risks: (RiskLevel | RiskLevelNoRisk)[] = riskLevels.length
    ? [...riskLevels, noRisk]
    : [];

  return {
    ...queryResult,
    riskLevels: risks,
    noRisk,
  };
};
